import type { RegisterBuxusRequest, RegistrationUrlParams } from '@buxus/shared'
import {
  RegistrationEventTypes,
  isConnectivityErrorMessage,
  isConnectivityFailure,
  outcomeFailureProps,
  outcomeSuccessProps,
  type AnalyticsFailureKind,
} from '@buxus/shared'
import type { ZodError } from 'zod'

import { isAbpHttpError } from '../abp/httpClient'
import type { CheckUserExistsDto } from '../../registration/registrationApi'
import {
  accountSetupCrm,
  findUserByEmailInBuxus,
  registerBuxusAccount,
} from '../../registration/registrationApi'
import type { RegistrationUiStep } from '../../registration/stepNavigation'
import { uiStepToEngineStep } from '../../registration/stepNavigation'

import { trackEvent } from './webAnalytics'

/** Structured fields for Mixpanel registration failure analysis. */
function failureAnalyticsFields(error: unknown): Record<string, unknown> {
  if (isAbpHttpError(error)) {
    const code = error.abpErrorCode ?? ''
    let failureKind: AnalyticsFailureKind = 'http'
    if (code.includes('DuplicateUserName') || code.includes('DuplicateEmail')) {
      failureKind = 'duplicate_account'
    } else if (code.includes('PasswordRequires') || code.includes('PasswordTooShort')) {
      failureKind = 'password_policy'
    } else if (code.includes('Validation') || error.message === 'Your request is not valid!') {
      failureKind = 'validation'
    }
    return {
      ...outcomeFailureProps({
        errorMessage: error.message,
        failureKind,
        httpStatus: error.httpStatus,
      }),
      abpErrorCode: error.abpErrorCode ?? undefined,
      abpErrorDetails: error.abpErrorDetails ?? undefined,
    }
  }
  if (isConnectivityFailure(error)) {
    return outcomeFailureProps({
      errorMessage: error instanceof Error ? error.message : String(error),
      failureKind: 'connectivity',
    })
  }
  return outcomeFailureProps({
    errorMessage: error instanceof Error ? error.message : String(error),
    failureKind: 'unknown',
  })
}

/** Campaign / host context merged into registration funnel events (legacy buxusweb parity). */
export type RegistrationFlowAnalyticsContext = {
  mode: 'standard' | 'express'
  isExpressReturnMode: boolean
  eventCode: string | null
  eventTitle: string | null
  promptPassword: boolean
  approvalCodePresent: boolean
  skipQuickCodePrompt: boolean
  version: string | null
  build: string | null
  isWebView: boolean
}

export type RegistrationStepAnalyticsContext = RegistrationFlowAnalyticsContext & {
  totalSteps: number
  stakeholderType?: string
  emailAddress?: string
  hasQuickCode: boolean
  hasReferrerContactId: boolean
}

const UI_STEP_NAMES: Record<RegistrationUiStep, string> = {
  quickCode: 'Quick Code Prompt',
  quickCodeEntry: 'Quick Code Entry',
  expressReturnQuickCode: 'Express Return Quick Code',
  quickCodeAlreadyRegistered: 'Quick Code Already Registered',
  accountBasics: 'Account Basics',
  mailing: 'Mailing Address',
  jurisdictions: 'Jurisdictions',
  organization: 'Organization',
  agencyInfo: 'Agency Info',
  agencyCapability: 'Agency Capabilities',
  agencyProcedure: 'Agency Procedures',
  agencyTraining: 'Agency Training',
  dispatchCapabilities: 'Dispatch Capabilities',
  agencyAgreement: 'Mutual Aid',
  additionalQuestions: 'Additional Questions',
  done: 'Complete',
}

export function registrationFlowContextFromUrl(
  urlParams: RegistrationUrlParams,
  isWebView: boolean,
): RegistrationFlowAnalyticsContext {
  return {
    mode: urlParams.isExpressMode ? 'express' : 'standard',
    isExpressReturnMode: urlParams.isExpressReturnMode,
    eventCode: urlParams.eventCode || null,
    eventTitle: urlParams.eventTitle || null,
    promptPassword: urlParams.promptPassword,
    approvalCodePresent: !!urlParams.approvalCode?.trim(),
    skipQuickCodePrompt: urlParams.skipQuickCodePrompt,
    version: urlParams.version,
    build: urlParams.build,
    isWebView,
  }
}

export function stepAnalyticsId(step: RegistrationUiStep): string {
  return uiStepToEngineStep(step) ?? step
}

export function stepAnalyticsName(step: RegistrationUiStep): string {
  return UI_STEP_NAMES[step] ?? step
}

function sessionFields(ctx: RegistrationFlowAnalyticsContext): Record<string, unknown> {
  return {
    mode: ctx.mode,
    isExpressReturnMode: ctx.isExpressReturnMode,
    eventCode: ctx.eventCode ?? undefined,
    eventTitle: ctx.eventTitle ?? undefined,
    promptPassword: ctx.promptPassword,
    approvalCodePresent: ctx.approvalCodePresent,
    skipQuickCodePrompt: ctx.skipQuickCodePrompt,
    ...(ctx.version ? { version: ctx.version } : {}),
    ...(ctx.build ? { build: ctx.build } : {}),
    isWebView: ctx.isWebView,
  }
}

export function trackRegistrationPageViewed(
  screen: string,
  ctx: RegistrationFlowAnalyticsContext,
  extra?: Record<string, unknown>,
): void {
  trackEvent(RegistrationEventTypes.PAGE_VIEWED, {
    screen,
    ...sessionFields(ctx),
    ...extra,
  })
}

export function trackRegistrationStepChanged(payload: {
  direction: 'initial' | 'forward' | 'backward' | 'jump'
  previousStep: RegistrationUiStep | null
  currentStep: RegistrationUiStep
  ctx: RegistrationStepAnalyticsContext
}): void {
  const { direction, previousStep, currentStep, ctx } = payload
  trackEvent(RegistrationEventTypes.REGISTRATION_STEP_CHANGED, {
    previousStepId: previousStep ? stepAnalyticsId(previousStep) : null,
    previousStepName: previousStep ? stepAnalyticsName(previousStep) : null,
    currentStepId: stepAnalyticsId(currentStep),
    currentStepName: stepAnalyticsName(currentStep),
    direction,
    totalSteps: ctx.totalSteps,
    stakeholderType: ctx.stakeholderType,
    emailAddress: ctx.emailAddress,
    hasQuickCode: ctx.hasQuickCode,
    hasReferrerContactId: ctx.hasReferrerContactId,
    ...sessionFields(ctx),
  })
}

export function flattenZodFieldErrors(error: ZodError): { errorFields: string[]; errorMessages: string[] } {
  const fieldErrors = error.flatten().fieldErrors as Record<string, string[] | undefined>
  const errorFields = Object.keys(fieldErrors)
  const errorMessages = errorFields.flatMap((key) => fieldErrors[key] ?? [])
  return { errorFields, errorMessages }
}

export function trackRegistrationFormValidationFailed(
  payload: {
    formName: string
    errorFields: string[]
    errorMessages: string[]
  } & RegistrationStepAnalyticsContext,
): void {
  const { formName, errorFields, errorMessages, ...ctx } = payload
  trackEvent(RegistrationEventTypes.REGISTRATION_FORM_VALIDATION_FAILED, {
    formName,
    errorFields,
    errorMessages,
    stakeholderType: ctx.stakeholderType,
    emailAddress: ctx.emailAddress,
    hasQuickCode: ctx.hasQuickCode,
    hasReferrerContactId: ctx.hasReferrerContactId,
    ...sessionFields(ctx),
  })
}

export function trackRegistrationQuickCodeFetched(
  payload: {
    quickCode: string
    hasData: boolean
    stakeholderType?: string
    email?: string
  } & RegistrationFlowAnalyticsContext,
): void {
  const { quickCode, hasData, stakeholderType, email, ...ctx } = payload
  trackEvent(RegistrationEventTypes.REGISTRATION_QUICK_CODE_FETCHED, {
    quickCode,
    hasData,
    stakeholderType,
    email,
    ...sessionFields(ctx),
  })
}

export function trackRegistrationReferrerContactFetched(
  payload: {
    referrerContactId: string
    hasData: boolean
    stakeholderType?: string
    email?: string
  } & RegistrationFlowAnalyticsContext,
): void {
  const { referrerContactId, hasData, stakeholderType, email, ...ctx } = payload
  trackEvent(RegistrationEventTypes.REGISTRATION_REFERRER_CONTACT_FETCHED, {
    referrerContactId,
    hasData,
    stakeholderType,
    email,
    ...sessionFields(ctx),
  })
}

export function trackRegistrationErrorOccurred(
  error: string,
  ctx: RegistrationFlowAnalyticsContext,
  extra?: Record<string, unknown>,
): void {
  const errorMessage =
    typeof extra?.errorMessage === 'string' ? extra.errorMessage : error
  const failureKindFromExtra =
    typeof extra?.failureKind === 'string' ? (extra.failureKind as AnalyticsFailureKind) : undefined
  const failureKind =
    failureKindFromExtra ??
    (isConnectivityErrorMessage(errorMessage) ? 'connectivity' : 'unknown')

  trackEvent(RegistrationEventTypes.ERROR_OCCURRED, {
    ...outcomeFailureProps({
      errorMessage,
      failureKind,
    }),
    // Keep legacy `error` code for existing Mixpanel boards (supplemental to failureKind).
    error,
    ...sessionFields(ctx),
    ...extra,
  })
}

export async function findUserByEmailInBuxusTracked(
  emailAddress: string,
  ctx: RegistrationFlowAnalyticsContext,
): Promise<CheckUserExistsDto | null> {
  try {
    const result = await findUserByEmailInBuxus(emailAddress)
    const zohoCrmId = result?.zohoCrmId?.trim() || result?.zohoId?.trim() || undefined
    trackEvent(RegistrationEventTypes.REGISTRATION_USER_EXISTS_CHECK, {
      emailAddress,
      userExists: !!result,
      userId: result?.userId?.trim() || undefined,
      zohoCrmId,
      hasZohoCrmId: !!zohoCrmId,
      ...sessionFields(ctx),
    })
    return result
  } catch (error) {
    trackRegistrationErrorOccurred('user_exists_check_failed', ctx, {
      emailAddress,
      errorMessage: error instanceof Error ? error.message : String(error),
    })
    throw error
  }
}

export async function registerBuxusAccountTracked(
  input: RegisterBuxusRequest,
  ctx: RegistrationStepAnalyticsContext & { legacyZohoId?: string },
): Promise<{ canLogin?: boolean; userId?: string }> {
  try {
    const result = await registerBuxusAccount(input)
    trackEvent(RegistrationEventTypes.REGISTRATION_SUCCESS, {
      emailAddress: input.emailAddress,
      username: input.username,
      canLogin: result.canLogin,
      userId: result.userId,
      stakeholderType: ctx.stakeholderType,
      hasQuickCode: ctx.hasQuickCode,
      hasReferrerContactId: ctx.hasReferrerContactId,
      ...outcomeSuccessProps(),
      ...sessionFields(ctx),
    })
    return result
  } catch (error) {
    trackEvent(RegistrationEventTypes.REGISTRATION_FAILED, {
      emailAddress: input.emailAddress,
      username: input.username,
      stakeholderType: ctx.stakeholderType,
      hasQuickCode: ctx.hasQuickCode,
      hasReferrerContactId: ctx.hasReferrerContactId,
      ...failureAnalyticsFields(error),
      ...sessionFields(ctx),
    })
    throw error
  }
}

type CrmResponseFields = {
  success: boolean
  responseCode?: string
  zohoCrmId?: string
  responseDataMessage?: string
  responseDataStatus?: string
  responseMessage?: string
  responseStatus?: string
  resolvedStatus?: string
}

function extractCrmResponseFields(res: unknown): CrmResponseFields {
  if (!res || typeof res !== 'object') {
    return { success: false }
  }
  const r = res as {
    data?: Array<{
      code?: string
      message?: string
      status?: string
      details?: { id?: string }
    }>
    message?: string
    status?: string
    resolvedStatus?: string
  }
  const row = r.data?.[0]
  const responseCode = row?.code
  return {
    success: responseCode === 'SUCCESS',
    responseCode,
    zohoCrmId: row?.details?.id,
    responseDataMessage: row?.message,
    responseDataStatus: row?.status,
    responseMessage: r.message,
    responseStatus: r.status,
    resolvedStatus:
      (typeof r.resolvedStatus === 'string' && r.resolvedStatus.trim()) ||
      (typeof row?.status === 'string' && row.status.trim()) ||
      undefined,
  }
}

export function trackRegistrationCrmAccountSetup(
  res: unknown,
  payload: {
    userId?: string
    emailAddress?: string
    stakeholderType?: string
    hasQuickCode: boolean
    hasReferrerContactId: boolean
    legacyZohoId?: string
  } & RegistrationFlowAnalyticsContext,
): CrmResponseFields {
  const fields = extractCrmResponseFields(res)
  const { userId, emailAddress, stakeholderType, hasQuickCode, hasReferrerContactId, legacyZohoId, ...ctx } =
    payload
  trackEvent(RegistrationEventTypes.REGISTRATION_CRM_ACCOUNT_SETUP, {
    userId,
    emailAddress,
    stakeholderType,
    status: fields.resolvedStatus,
    resolvedStatus: fields.resolvedStatus,
    success: fields.success,
    responseCode: fields.responseCode,
    zohoCrmId: fields.zohoCrmId,
    responseDataMessage: fields.responseDataMessage,
    responseDataStatus: fields.responseDataStatus,
    responseMessage: fields.responseMessage,
    responseStatus: fields.responseStatus,
    hasQuickCode,
    hasReferrerContactId,
    legacyZohoId,
    ...sessionFields(ctx),
  })
  return fields
}

export function trackRegistrationCrmAccountSetupFailed(
  res: unknown,
  payload: {
    userId?: string
    emailAddress?: string
    stakeholderType?: string
    errorMessage: string
    failureKind?: AnalyticsFailureKind
    httpStatus?: number
    abpErrorCode?: string
    hasQuickCode: boolean
    hasReferrerContactId: boolean
    legacyZohoId?: string
  } & RegistrationFlowAnalyticsContext,
): void {
  const fields = extractCrmResponseFields(res)
  const {
    userId,
    emailAddress,
    stakeholderType,
    errorMessage,
    failureKind,
    httpStatus,
    abpErrorCode,
    hasQuickCode,
    hasReferrerContactId,
    legacyZohoId,
    ...ctx
  } = payload
  trackEvent(RegistrationEventTypes.REGISTRATION_CRM_ACCOUNT_SETUP_FAILED, {
    userId,
    emailAddress,
    stakeholderType,
    status: fields.resolvedStatus,
    resolvedStatus: fields.resolvedStatus,
    responseCode: fields.responseCode,
    zohoCrmId: fields.zohoCrmId,
    responseDataMessage: fields.responseDataMessage,
    responseDataStatus: fields.responseDataStatus,
    responseMessage: fields.responseMessage,
    responseStatus: fields.responseStatus,
    ...outcomeFailureProps({
      errorMessage,
      failureKind: failureKind ?? 'unknown',
      httpStatus,
    }),
    ...(abpErrorCode ? { abpErrorCode } : {}),
    hasQuickCode,
    hasReferrerContactId,
    legacyZohoId,
    ...sessionFields(ctx),
  })
}

export async function accountSetupCrmTracked(
  body: Record<string, unknown>,
  payload: {
    userId?: string
    emailAddress?: string
    stakeholderType?: string
    hasQuickCode: boolean
    hasReferrerContactId: boolean
    legacyZohoId?: string
  } & RegistrationFlowAnalyticsContext,
): Promise<unknown> {
  try {
    const res = await accountSetupCrm(body)
    const fields = trackRegistrationCrmAccountSetup(res, payload)
    if (!fields.success) {
      trackRegistrationCrmAccountSetupFailed(res, {
        ...payload,
        errorMessage: fields.responseDataMessage || fields.responseMessage || 'Unknown error',
        failureKind: 'http',
      })
    }
    return res
  } catch (error) {
    const fields = failureAnalyticsFields(error)
    trackRegistrationCrmAccountSetupFailed(null, {
      ...payload,
      errorMessage: String(fields.errorMessage ?? 'Unknown error'),
      failureKind: (fields.failureKind as AnalyticsFailureKind | undefined) ?? 'unknown',
      httpStatus: typeof fields.httpStatus === 'number' ? fields.httpStatus : undefined,
      abpErrorCode: typeof fields.abpErrorCode === 'string' ? fields.abpErrorCode : undefined,
    })
    throw error
  }
}

export function trackRegistrationFormSubmitted(
  formName: string,
  ctx: RegistrationFlowAnalyticsContext,
  extra?: Record<string, unknown>,
): void {
  trackEvent(RegistrationEventTypes.FORM_SUBMITTED, {
    formName,
    ...sessionFields(ctx),
    ...extra,
  })
}

export function trackRegistrationButtonPressed(
  label: string,
  ctx: RegistrationFlowAnalyticsContext,
  extra?: Record<string, unknown>,
): void {
  trackEvent(RegistrationEventTypes.BUTTON_PRESSED, {
    label,
    ...sessionFields(ctx),
    ...extra,
  })
}
